import { getAuth, onAuthStateChanged, User } from 'firebase/auth'
import { app } from './firebase'
import { getUserIdByGoogleId } from './userProfile'

const auth = getAuth(app)

// Global auth state
let currentGoogleUser: User | null = null
let currentUserId: string | null = null
let authInitialized = false
let authSettled = false
let resolveAuthReady: (userId: string | null) => void = () => {}
const authReady = new Promise<string | null>((resolve) => {
  resolveAuthReady = resolve
})
let authCallbacks: Array<(userId: string | null) => void> = []

function settleAuth(userId: string | null) {
  if (authSettled) return
  authSettled = true
  resolveAuthReady(userId)
}

/**
 * Initialize Google Auth listener
 * Should be called once when app starts
 */
export function initGoogleAuth(): void {
  if (authInitialized) {
    return
  }

  authInitialized = true

  onAuthStateChanged(auth, async (user) => {
    currentGoogleUser = user

    if (user) {
      // User is signed in with Google
      console.log('Google user signed in:', user.email)

      // Get the userId associated with this Google account
      const userId = await getUserIdByGoogleId(user.uid)

      if (userId) {
        console.log('Found existing userId for Google account:', userId)
        currentUserId = userId
        settleAuth(userId)

        // Notify all callbacks
        authCallbacks.forEach((callback) => callback(userId))
      } else {
        // No existing profile linked to this Google account
        // Don't create anything automatically - user needs to link manually
        console.log('No existing profile found for this Google account')
        currentUserId = null
        settleAuth(null)
        authCallbacks.forEach((callback) => callback(null))
      }
    } else {
      // User is signed out
      console.log('Google user signed out')
      currentUserId = null
      settleAuth(null)
      authCallbacks.forEach((callback) => callback(null))
    }
  })
}

export function waitForGoogleUserId(): Promise<string | null> {
  initGoogleAuth()
  return Promise.race([
    authReady,
    new Promise<string | null>((resolve) => {
      setTimeout(() => resolve(currentUserId), 5000)
    }),
  ])
}

/**
 * Get current Google user
 */
export function getCurrentGoogleUser(): User | null {
  return currentGoogleUser
}

/**
 * Get current userId from Google account
 */
export function getCurrentUserId(): string | null {
  return currentUserId
}

/**
 * Check if user is signed in with Google
 */
export function isSignedInWithGoogle(): boolean {
  return currentGoogleUser !== null
}

/**
 * Register a callback to be called when auth state changes
 */
export function onAuthChange(
  callback: (userId: string | null) => void
): () => void {
  authCallbacks.push(callback)

  // Call immediately with current state
  if (authInitialized && currentUserId) {
    callback(currentUserId)
  }

  // Return unsubscribe function
  return () => {
    authCallbacks = authCallbacks.filter((cb) => cb !== callback)
  }
}

/**
 * Sign out from Google
 */
export async function signOutFromGoogle(): Promise<void> {
  try {
    await auth.signOut()
    currentGoogleUser = null
    currentUserId = null
  } catch (error) {
    console.error('Error signing out:', error)
    throw error
  }
}
